import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink, ArrowRight, ImageIcon } from 'lucide-react';

// =====================================================================
// AVISO DE TEXTO — pop-up genérico (avisos cadastrados no Admin)
// =====================================================================
// É o CONTEÚDO dos avisos de TEXTO da Fase 2 (os que moram na dtbAvisos e são
// criados pelo Admin). Diferente do AvisoHomeTour — que "desenha" a Home e é
// fixo em código — este é genérico: imagem opcional no topo, título, data, o
// corpo com FORMATAÇÃO LEVE e, se houver, um botão de link.
//
// Props (o SinoAvisos passa o payload do próprio aviso vindo do GET /api/avisos):
//   - titulo    : título do aviso (cabeçalho do pop-up)
//   - data      : data de exibição (ex.: '10/07/2026')
//   - corpo     : texto do aviso (formatação leve — ver renderCorpo)
//   - imagemUrl : (opcional) caminho de uma imagem servida por HTTP (banner/print)
//   - linkUrl   : (opcional) destino do botão; caminho interno navega DENTRO do
//                 portal, URL externa (http…) abre em aba nova
//   - linkTexto : (opcional) rótulo do botão; cai para "Abrir" se vazio
//   - onClose   : fecha o pop-up (X, fundo e botão "Entendi")
//
// Marcar como lido é responsabilidade de quem abre o pop-up (o SinoAvisos
// dispara o POST /ler) — este componente é só visual/navegação.
// ---------------------------------------------------------------------

const COR = {
    vermelhoEscuro: '#990605',
    amarelo: '#FDBB20',
    texto: '#1a1a1a',
    cinzaTexto: '#555',
    cinzaSuave: '#7a7a7a',
    borda: 'rgba(199,194,194,0.45)',
    molduraFundo: '#f4f3f3',
};
const FONTE = "'Raleway', sans-serif";

// ---------------------------------------------------------------------
// FORMATAÇÃO LEVE do corpo (sem dependência de Markdown):
//   - linha em branco separa PARÁGRAFOS;
//   - um bloco cujas linhas começam TODAS com "- " vira LISTA com marcadores;
//   - **texto** dentro de qualquer linha fica em NEGRITO;
//   - quebra de linha simples dentro de um parágrafo vira <br/>.
// Cobre o essencial de um aviso sem editor pesado. Mantido simples de propósito.
// ---------------------------------------------------------------------

// Trata **negrito** numa string, devolvendo um array de nós React.
function renderInline(texto) {
    // Divide preservando os delimitadores: segmentos ímpares estavam entre **.
    const partes = texto.split(/\*\*(.+?)\*\*/g);
    return partes.map((p, i) =>
        i % 2 === 1 ? <strong key={i}>{p}</strong> : <React.Fragment key={i}>{p}</React.Fragment>
    );
}

// Converte o corpo em blocos (parágrafos e listas) já formatados.
function renderCorpo(corpo) {
    if (!corpo) return null;
    // Blocos separados por uma ou mais linhas em branco.
    const blocos = corpo.replace(/\r\n/g, '\n').split(/\n\s*\n/);

    return blocos.map((bloco, bi) => {
        const linhas = bloco.split('\n').filter((l) => l.trim() !== '');
        if (linhas.length === 0) return null;

        // Lista: todas as linhas do bloco começam com "- ".
        const ehLista = linhas.every((l) => /^\s*-\s+/.test(l));
        if (ehLista) {
            return (
                <ul key={bi} style={{ margin: '0 0 12px', paddingLeft: '20px' }}>
                    {linhas.map((l, li) => (
                        <li key={li} style={{ marginBottom: '4px', lineHeight: 1.55 }}>
                            {renderInline(l.replace(/^\s*-\s+/, ''))}
                        </li>
                    ))}
                </ul>
            );
        }

        // Parágrafo: quebras simples viram <br/>.
        return (
            <p key={bi} style={{ margin: '0 0 12px', lineHeight: 1.6 }}>
                {linhas.map((l, li) => (
                    <React.Fragment key={li}>
                        {renderInline(l)}
                        {li < linhas.length - 1 && <br />}
                    </React.Fragment>
                ))}
            </p>
        );
    });
}

// ---------------------------------------------------------------------
// Imagem do topo com degradê gracioso: se o arquivo ainda não existe (ex.: o
// print ainda não foi solto em public/avisos/), mostra uma moldura "em breve"
// em vez de um ícone quebrado — o aviso já funciona antes da imagem chegar.
// ---------------------------------------------------------------------
function ImagemAviso({ src, alt }) {
    const [erro, setErro] = useState(false);
    if (erro) {
        return (
            <div style={{
                width: '100%', height: '160px', borderRadius: '10px',
                backgroundColor: COR.molduraFundo, border: `1px dashed ${COR.borda}`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '8px', color: COR.cinzaSuave, marginBottom: '18px',
            }}>
                <ImageIcon size={26} />
                <span style={{ fontSize: '13px', fontWeight: 500 }}>Imagem em breve</span>
            </div>
        );
    }
    // maxWidth (não width): não AMPLIA imagem pequena (ícone ~240px ficaria
    // borrado esticado); imagem grande (banner/print) encolhe pra caber.
    // Centralizada, com teto de altura pra não dominar o pop-up.
    return (
        <img
            src={src}
            alt={alt}
            onError={() => setErro(true)}
            style={{
                maxWidth: '100%', maxHeight: '320px', display: 'block', margin: '0 auto 18px',
                borderRadius: '10px', border: `1px solid ${COR.borda}`,
            }}
        />
    );
}

function AvisoTexto({ titulo, data, corpo, imagemUrl, linkUrl, linkTexto, onClose }) {
    const navigate = useNavigate();

    // Link interno ('/financeiro?tab=troco') navega DENTRO do portal (fecha o
    // pop-up e vai pra tela); URL externa abre em aba nova.
    const linkExterno = /^https?:\/\//i.test(linkUrl || '');
    const abrirLink = () => {
        if (!linkUrl) return;
        if (linkExterno) {
            window.open(linkUrl, '_blank', 'noopener,noreferrer');
        } else {
            onClose();
            navigate(linkUrl);
        }
    };

    return (
        // Fundo escurecido — clicar fora fecha.
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1000, padding: '16px', fontFamily: FONTE,
            }}
        >
            {/* Caixa do pop-up — stopPropagation pra clicar dentro não fechar. */}
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: '#fff', borderRadius: '14px', width: '100%', maxWidth: '560px',
                    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.28)', overflow: 'hidden',
                }}
            >
                {/* Cabeçalho */}
                <div style={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    gap: '12px', padding: '22px 24px 18px', borderBottom: `1px solid ${COR.borda}`,
                }}>
                    <div>
                        <div style={{
                            fontSize: '12px', fontWeight: 700, color: COR.amarelo,
                            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px',
                        }}>
                            Aviso
                        </div>
                        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: COR.texto }}>
                            {titulo}
                        </h2>
                        {data && (
                            <p style={{ margin: '6px 0 0', fontSize: '13px', color: COR.cinzaSuave, fontWeight: 500 }}>
                                {data}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        title="Fechar"
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer', color: COR.cinzaSuave,
                            display: 'flex', padding: '4px', flexShrink: 0,
                        }}
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Corpo rolável: imagem (opcional) + texto formatado. */}
                <div style={{ overflowY: 'auto', padding: '20px 24px' }}>
                    {imagemUrl && <ImagemAviso src={imagemUrl} alt={titulo} />}

                    <div style={{ fontSize: '15px', color: COR.cinzaTexto, fontWeight: 400 }}>
                        {renderCorpo(corpo)}
                    </div>

                    {/* Botão de link opcional */}
                    {linkUrl && (
                        <button
                            type="button"
                            onClick={abrirLink}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '6px',
                                backgroundColor: COR.amarelo, color: COR.texto, border: 'none',
                                borderRadius: '8px', padding: '10px 18px', fontSize: '14px', fontWeight: 700,
                                cursor: 'pointer', fontFamily: FONTE,
                            }}
                        >
                            {linkTexto || 'Abrir'}
                            {linkExterno ? <ExternalLink size={16} /> : <ArrowRight size={16} />}
                        </button>
                    )}
                </div>

                {/* Rodapé com o botão de fechar */}
                <div style={{
                    padding: '16px 24px', borderTop: `1px solid ${COR.borda}`,
                    display: 'flex', justifyContent: 'flex-end',
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            backgroundColor: COR.vermelhoEscuro, color: '#fff', border: 'none',
                            borderRadius: '8px', padding: '11px 24px', fontSize: '15px', fontWeight: 600,
                            cursor: 'pointer', fontFamily: FONTE,
                        }}
                    >
                        Entendi
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AvisoTexto;
